import type { ColDef } from 'ag-grid-community';
import type { TFunction } from 'i18next';

import { ScoreCategory, ScoreModuleState, type ScoreModuleStatus } from '../TreamsScore.types';
import {
	type GridRow,
	ManagerCell,
	NameCell,
	type ScoreColors,
	ScoreColumnCell,
	type ScoreColumnId,
	TotalScoreColumnCell,
	centeredCell,
	isDivider,
	leftCell,
	rowScore,
	rowTotal,
} from './cells';

// Leading order for the score factors; the card follows this order too.
export const SCORE_CATEGORY_ORDER: ScoreCategory[] = [
	ScoreCategory.Engagement,
	ScoreCategory.OneOnOne,
	ScoreCategory.Targets,
	ScoreCategory.Feedback,
	ScoreCategory.Conversations,
];

type BuildColumnDefsArgs = {
	columnLabels: Record<ScoreColumnId, string>;
	getScoreColors: (score: number) => ScoreColors;
	moduleStatus: ScoreModuleStatus;
	nameColumnHeader?: string;
	nameColumnWidth: number;
	showManagerColumn?: boolean;
	t: TFunction;
};

export function buildColumnDefs({
	columnLabels,
	getScoreColors,
	moduleStatus,
	nameColumnHeader,
	nameColumnWidth,
	showManagerColumn = true,
	t,
}: BuildColumnDefsArgs): ColDef<GridRow>[] {
	const metricColumn = (headerName: string, key: ScoreCategory): ColDef<GridRow> => {
		const state = moduleStatus[key];
		return {
			headerName,
			colId: key,
			flex: 0,
			width: 120,
			sortable: state === ScoreModuleState.Enabled,
			cellStyle: centeredCell,
			headerComponentParams: {
				align: 'center',
				titleColor: state === ScoreModuleState.Unavailable ? '#D8E0E8' : undefined,
			},
			valueGetter: ({ data }) => rowScore(data, key),
			cellRenderer: ScoreColumnCell,
			cellRendererParams: { scoreKey: key, state, getScoreColors },
		};
	};

	const managerColumn: ColDef<GridRow> = {
		headerName: t('analytics.treamsScore.column.manager'),
		flex: 1,
		minWidth: 200,
		valueGetter: ({ data }) =>
			data && !isDivider(data)
				? data.managers.map((manager) => `${manager.firstName} ${manager.lastName}`).join(', ')
				: '',
		cellRenderer: ManagerCell,
	};

	return [
		{
			headerName: nameColumnHeader ?? t('analytics.treamsScore.column.departments'),
			// Without the manager column no column flexes, leaving the grid
			// narrower than its container, so the name column takes the slack.
			flex: showManagerColumn ? 0 : 1,
			width: nameColumnWidth,
			minWidth: nameColumnWidth,
			valueGetter: ({ data }) => (data && !isDivider(data) ? data.name : ''),
			cellRenderer: NameCell,
		},
		{
			headerName: columnLabels.total,
			colId: 'total',
			flex: 0,
			width: 180,
			// initialSort (not sort): the table re-renders on sort changes, which
			// makes AG Grid re-apply column defs — a plain `sort` would then undo
			// the user's chosen sort every time.
			initialSort: 'desc',
			cellStyle: leftCell,
			valueGetter: ({ data }) => rowTotal(data),
			cellRenderer: TotalScoreColumnCell,
		},
		...(showManagerColumn ? [managerColumn] : []),
		...SCORE_CATEGORY_ORDER.map((key) => metricColumn(columnLabels[key], key)),
	];
}
